/**
 * Hook for submitting and tracking loan requests.
 * Persists to both the backend (/api/loans) and localStorage for instant local reads.
 */

import { useState, useEffect, useCallback } from 'react'

export type LoanRequestStatus =
  | 'requested'
  | 'bank_review'
  | 'kyc'
  | 'disbursement'
  | 'active'
  | 'repaid'
  | 'rejected'

export interface LoanRequest {
  id: string
  locId: string | null
  borrowerAddress: string
  bankPartner: string | null
  requestedAmount: number
  status: LoanRequestStatus
  createdAt: string
  updatedAt: string
}

const LS_KEY = 'loan_requests'

// ── localStorage helpers ──────────────────────────────────────────────────────

function readLocalRequests(): LoanRequest[] {
  try {
    const raw = localStorage.getItem(LS_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function writeLocalRequests(requests: LoanRequest[]) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(requests))
  } catch {
    // storage quota exceeded — silently ignore
  }
}

// ── Submit a loan request ─────────────────────────────────────────────────────

export async function submitLoanRequest(params: {
  locId?: string
  borrowerAddress: string
  bankPartner?: string
  requestedAmount: number
}): Promise<LoanRequest> {
  const payload = {
    locId: params.locId ?? null,
    borrowerAddress: params.borrowerAddress,
    bankPartner: params.bankPartner ?? null,
    requestedAmount: params.requestedAmount,
    status: 'requested',
  }

  let loan: LoanRequest

  // 1. Try backend (best effort — may be offline in dev)
  try {
    const resp = await fetch('/api/loans', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: AbortSignal.timeout(5000),
    })

    if (resp.ok) {
      const data = await resp.json()
      loan = data.loan as LoanRequest
    } else {
      throw new Error(`Backend ${resp.status}`)
    }
  } catch (err) {
    console.warn('[useLoanRequest] Backend unavailable, creating local record:', err)
    // Fallback: create a local-only record
    loan = {
      id: `loan_local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      ...payload,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    } as LoanRequest
  }

  // 2. Always persist to localStorage
  const existing = readLocalRequests()
  // Avoid duplicates if backend already stored it
  if (!existing.find(r => r.id === loan.id)) {
    writeLocalRequests([...existing, loan])
  }

  return loan
}

// ── Hook: get loan requests for an address ────────────────────────────────────

export function useLoanRequests(borrowerAddress?: string) {
  const [requests, setRequests] = useState<LoanRequest[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const load = useCallback(async () => {
    if (!borrowerAddress) {
      setRequests([])
      return
    }

    setIsLoading(true)
    const addr = borrowerAddress.toLowerCase()

    // Always show localStorage immediately
    const local = readLocalRequests().filter(r => r.borrowerAddress === addr)
    setRequests(local)

    // Then try to sync from backend
    try {
      const resp = await fetch(`/api/loans/${addr}`, {
        signal: AbortSignal.timeout(5000),
      })
      if (resp.ok) {
        const data = await resp.json()
        const serverLoans: LoanRequest[] = data.loans ?? []

        // Merge: server is source of truth, but include any local-only records
        const localIds = new Set(serverLoans.map(l => l.id))
        const localOnly = readLocalRequests().filter(
          r => r.borrowerAddress === addr && !localIds.has(r.id)
        )
        const merged = [...serverLoans, ...localOnly]
        merged.sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )

        setRequests(merged)
        writeLocalRequests([
          // Replace same-address records with merged
          ...readLocalRequests().filter(r => r.borrowerAddress !== addr),
          ...merged,
        ])
      }
    } catch {
      // Backend offline — local data is fine
    } finally {
      setIsLoading(false)
    }
  }, [borrowerAddress])

  useEffect(() => {
    load()
  }, [load])

  return { requests, isLoading, refetch: load }
}

// ── Hook: submit a loan request ───────────────────────────────────────────────

export function useSubmitLoanRequest() {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const submit = useCallback(
    async (params: Parameters<typeof submitLoanRequest>[0]) => {
      setIsSubmitting(true)
      setError(null)
      try {
        const loan = await submitLoanRequest(params)
        return loan
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err))
        setError(e)
        throw e
      } finally {
        setIsSubmitting(false)
      }
    },
    []
  )

  return { submit, isSubmitting, error }
}
